import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CloudInfo, IdleState, Machine, MachineStop } from '@couch/shared';
import { api, errorMessage } from '../../api/client';
import { CloudBadge } from '../machines/CloudBadge';
import { statusKey } from './StatusPanel';

const CHOICES: readonly [number, string][] = [[0, 'Never'], [15, '15 minutes'], [30, '30 minutes'], [60, '1 hour'], [120, '2 hours'], [240, '4 hours']];

/** Why a machine cannot stop itself (doc 52). */
const CANNOT_STOP: Readonly<Record<Exclude<CloudInfo['selfStop'], 'ok'>, string>> = Object.freeze({
  'needs-sudo': 'This machine cannot stop itself: the user Couch runs as may not use sudo without a password. Stop it in your provider\'s console.',
  'missing-key': 'This pod cannot stop itself: RunPod gave it no API key. Stop it in the RunPod console.',
  unsupported: 'This machine cannot stop itself: shut down from inside, a Scaleway instance stays in standby and is still billed. Stop it in the Scaleway console.',
});

export function stoppedText(stop: MachineStop): string {
  const at = new Date(stop.at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  return `It stopped itself on ${at} after ${stop.idleMinutes} idle minutes.`;
}

function IdleSetting({ machineId, idle }: { machineId: string; idle: IdleState }) {
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (minutes: number) => api.setIdleStop(machineId, minutes),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: statusKey(machineId) }),
  });
  if (idle.selfStop !== 'ok') return <p role="alert" className="form-error">{CANNOT_STOP[idle.selfStop]}</p>;
  const known = CHOICES.some(([m]) => m === idle.minutes);
  return (
    <div className="field idle-setting">
      <label htmlFor="idle-stop">Stop after … without use</label>
      <select id="idle-stop" value={String(idle.minutes)} disabled={save.isPending} onChange={(e) => { save.mutate(Number(e.target.value)); }}>
        {!known && <option value={String(idle.minutes)}>{idle.minutes} minutes</option>}
        {CHOICES.map(([m, label]) => <option key={m} value={String(m)}>{label}</option>)}
      </select>
      <p className="muted">
        Idle for {Math.floor(idle.idleForMinutes)} minutes. Use means a picture or video being made, a download, or anything Couch asks the
        machine to do. Editing a workflow in ComfyUI without running it counts as idle.
      </p>
      {save.isError && <p role="alert" className="form-error">{errorMessage(save.error)}</p>}
    </div>
  );
}

/** A cloud machine on its page (docs 51, 52): what it is, its console, the warning, the idle self-stop. */
export function CloudPanel({ machine, cloud }: { machine: Machine; cloud: CloudInfo }) {
  const status = useQuery({ queryKey: statusKey(machine.id), queryFn: () => api.machineStatus(machine.id), enabled: machine.online, retry: false });
  const idle = status.data?.status.idle;
  return (
    <section className="panel" aria-labelledby="cloud-title">
      <h2 id="cloud-title">Cloud machine</h2>
      <CloudBadge cloud={cloud} />
      {machine.online && idle && <IdleSetting machineId={machine.id} idle={idle} />}
      {machine.lastStop && <p className="muted">{stoppedText(machine.lastStop)}</p>}
    </section>
  );
}
